export interface BuildWizardFlowSnapshotOptions {
  flow: any;
  steps?: any[];
  flowLines?: any[];
}

function cloneSerializable<T>(value: T): T | null {
  if (value === undefined) {
    return null;
  }

  try {
    return JSON.parse(JSON.stringify(value)) as T;
  } catch {
    return null;
  }
}

function compactActionDefinitions(definitions: any): Record<string, any> {
  if (!definitions || typeof definitions !== "object") {
    return {};
  }

  return Object.entries(definitions).reduce(
    (result, [key, value]: [string, any]) => {
      result[key] =
        value && typeof value === "object" && "value" in value
          ? value.value
          : value;
      return result;
    },
    {} as Record<string, any>
  );
}

function compactActions(actions: any): Record<string, any> {
  if (!actions || typeof actions !== "object") {
    return {};
  }

  return Object.entries(actions).reduce(
    (result, [key, action]: [string, any]) => {
      result[key] = {
        actionType: action?.actionType,
        actionDefinitions: compactActionDefinitions(action?.actionDefinitions),
        bodyActions: compactActionDefinitions(action?.bodyActions),
      };
      return result;
    },
    {} as Record<string, any>
  );
}

function parseWizardUiData(uiData: any): any {
  if (!uiData) {
    return undefined;
  }

  if (typeof uiData === "object") {
    return uiData.wizard || uiData;
  }

  if (typeof uiData !== "string") {
    return undefined;
  }

  try {
    const parsed = JSON.parse(uiData);
    return parsed?.wizard || parsed;
  } catch {
    return undefined;
  }
}

function compactConnection(connection: any): Record<string, any> | undefined {
  if (!connection || typeof connection !== "object") {
    return undefined;
  }

  return {
    id: connection.id,
    name: connection.name,
    connector: {
      name: connection.connector?.name,
      connectorSpecId: connection.connector?.connectorSpecId,
    },
  };
}

/** Condition rows, in the shape the backend stores and the wizard already knows how to read. */
function compactFilterRows(filter: any): any[] | undefined {
  if (!Array.isArray(filter) || !filter.length) {
    return undefined;
  }

  return filter.map((row: any) => ({
    firstInput: row?.firstInput,
    secondInput: row?.secondInput,
    condition: row?.condition,
    rule: row?.rule,
    leftParen: row?.leftParen,
    rightParen: row?.rightParen,
  }));
}

/**
 * A router's branches, each with its own nested steps.
 *
 * Without this the wizard is handed a router with no branches and no idea that steps live inside
 * it, so asking it to change anything makes it either re-invent the branches or ask what they
 * should contain. The Python side already walks `routes[].steps` when it reads the snapshot.
 */
function compactRoutes(routes: any): any[] | undefined {
  if (!Array.isArray(routes) || !routes.length) {
    return undefined;
  }

  return routes.map((route: any, index: number) => ({
    name: route?.name,
    order: typeof route?.order === "number" ? route.order : index,
    filter: compactFilterRows(route?.filter),
    steps: (Array.isArray(route?.steps) ? route.steps : []).map(
      (branchStep: any, branchIndex: number) =>
        compactStep(branchStep, branchIndex)
    ),
  }));
}

/**
 * The step's number in the flat, flow-wide sequence.
 *
 * `stepNumber` first, because `ix` is not unique: useAddPositionToStep overwrites ix as a layout
 * coordinate and gives a router's first branch step the same ix as the router itself, so deriving
 * the number from ix hands the model two different steps under one number — and a router's
 * branches are exactly what it needs to tell apart. EditVueFlow restores stepNumber from the
 * backend before that overwrite for this reason; ix stays as the fallback for anything older.
 */
function resolveStepNumber(step: any, index: number): number {
  if (typeof step?.stepNumber === "number") {
    return step.stepNumber;
  }
  if (typeof step?.ix === "number") {
    return step.ix + 1;
  }
  return index + 1;
}

function compactStep(step: any, index: number): Record<string, any> {
  const actions = compactActions(step?.actions);
  const actionIds = Object.keys(actions);

  return {
    id: step?.id,
    origId: step?.origId,
    ix: typeof step?.ix === "number" ? step.ix : index,
    step_number: resolveStepNumber(step, index),
    name: step?.name,
    connectorSpecId: step?.connectorSpecId,
    connector_id:
      step?.connector_id || step?.connectorId || step?.connectorSpecId,
    connection_id:
      step?.connection_id || step?.connectionId || step?.connection?.id,
    connection: compactConnection(step?.connection),
    action_id: actionIds[0],
    actions,
    contentPath: step?.contentPath,
    // Without the type, a router and a LOOP / LOOP_END virtual step both read as malformed
    // regular steps (no connector, no action), so the wizard cannot see the branch or the loop
    // it is being asked to revise.
    type: step?.type,
    loopType: step?.loopType,
    loopConfig: step?.loopConfig,
    uiData: parseWizardUiData(step?.uiData),
    continueOnFail: step?.continueOnFail,
    // Named as the wizard's own schema names it, not as the backend stores it. The model has to
    // re-emit this to keep it, and it will not connect a snapshot `delay` to a spec `delay_ms`.
    delay_ms: step?.delay,
    filter: compactFilterRows(step?.filter),
    routes: compactRoutes(step?.routes),
  };
}

function compactFlowLines(flowLines: any[] | undefined): any[] | undefined {
  if (!Array.isArray(flowLines)) {
    return undefined;
  }

  return flowLines.map((line) => ({
    id: line?.id,
    from: line?.from,
    to: line?.to,
    source: line?.source,
    target: line?.target,
    sourceHandle: line?.sourceHandle,
    targetHandle: line?.targetHandle,
  }));
}

export function buildWizardFlowSnapshot(
  options: BuildWizardFlowSnapshotOptions
): Record<string, any> | null {
  const serializedFlow = cloneSerializable(options.flow);

  if (!serializedFlow || typeof serializedFlow !== "object") {
    return null;
  }

  const flow = serializedFlow as Record<string, any>;
  const steps = Array.isArray(options.steps)
    ? options.steps.map((step, index) => compactStep(step, index))
    : [];

  // Key order is load-bearing, not cosmetic. The Python side caps the serialised snapshot at a
  // character budget, so whatever serialises last is what gets dropped when a flow is large.
  // `userDefinedVariables` is tiny (a few hundred bytes on a real flow) but every loop source and
  // parameter default may reference it, so it goes near the front. `flowLines` is by far the
  // largest key and is the most redundant — the step tree already carries type, routes and
  // nesting — so it goes last, where it is cheapest to lose.
  return {
    id: flow.id,
    flow_id: flow.flow_id,
    name: flow.name,
    description: flow.description,
    version: flow.version,
    flow_version: flow.flow_version,
    latest: flow.latest,
    newUi: flow.newUi,
    flowStatusType: flow.flowStatusType,
    continueOnFailure: flow.continueOnFailure,
    // Activation and schedule ride near the front deliberately. They are a few hundred bytes at
    // most, and leaving them out entirely is what let the assistant answer "the flow is currently
    // unscheduled" about a flow that was scheduled: the snapshot it reasons from carried no
    // schedule field at all, so it had nothing to read and guessed.
    active: flow.active,
    scheduleType: flow.scheduleType,
    schedule: flow.schedule,
    timeZone: flow.timeZone,
    userDefinedVariables: flow.userDefinedVariables,
    stepCount: steps.length,
    steps,
    flowLines: compactFlowLines(options.flowLines || flow.flowLines),
  };
}
